// src/helpers/client.js
// Shared axios client: attaches bearer on send, renews proactively when the JWT
// expires within 5 minutes (DE-9510), and falls back to one 401 refresh+retry
// for idempotent calls when the token is expired or the server signals
// invalid_token. Permission 401s are not masked — they still fail / logout.
// added to package.json - "axios": "axios/dist/node/axios.cjs",to force it to import the commonjs axios build so that it wouldn't error on this import statement
import * as Sentry from "@sentry/browser";
import axios from "axios";
import config from "config";
import { v4 as uuidv4 } from "uuid";

import { alertActions } from "../actions/alert.actions";
import { userActions } from "../actions/user.actions";
import { SILENT_REDIRECT_URI } from "../authConfig";
import msalInstance, { msalReady } from "../msalInstance";
import { store } from "../store/store";
import { getIsExchangesE2eSession } from "./cypressE2eFlags";
import { getIsCypressAut } from "./isCypressAut";
import {
  markMaintenanceHttp503Observed,
  markPortalApiRequestSuccess,
  normalize503Payload,
} from "./maintenanceProbe";
import { getSessionMsalAccount } from "./msalSession";
import { consumePostAuth401Fallback } from "./postAuthGrace";
import { syncSentryUserFromAdsUser } from "./sentryUser";
import {
  getAccessTokenFromStorage,
  getTokenExpiresInMs,
  tryParseJwt,
} from "./token";
import {
  isBearerExpired,
  serverSaysInvalidToken,
  shouldProactivelyRenewBearer,
  shouldRetry401WithTokenRefresh,
  shouldSkipSilentAcquire,
} from "./tokenRenew";
// Track MSAL initialization to avoid calling APIs before ready
let msalInitialized = false;
msalReady
  .then(() => {
    msalInitialized = true;
  })
  .catch(() => {
    msalInitialized = false;
  });

// Optional max wait for MSAL init before sending protected requests
const MSAL_INIT_WAIT_MS = 2500;

const isCypressRuntime = () => getIsCypressAut();
const skipSilentAcquire = () =>
  shouldSkipSilentAcquire(isCypressRuntime());

// Track navigation state for better error context
let isNavigating = false;
let navigationStartTime = null;

// Utility function to check if an error is due to request cancellation
const isCanceledRequest = (error) => {
  return (
    error.name === "AbortError" ||
    error.name === "CanceledError" ||
    error.code === "ERR_CANCELED"
  );
};

// Add cancellation context as breadcrumb when relevant
const addCancellationBreadcrumb = (error) => {
  if (isCanceledRequest(error)) {
    Sentry.addBreadcrumb({
      message: "Request canceled due to navigation",
      category: "navigation",
      level: "info",
      data: {
        url: error.config?.url,
        method: error.config?.method,
        errorName: error.name,
        errorCode: error.code,
        isNavigating: isNavigating,
        timeSinceNavigation: navigationStartTime
          ? Date.now() - navigationStartTime
          : null,
        timestamp: new Date().toISOString(),
      },
    });
    return true;
  }
  return false;
};

// Enhanced error reporting that includes cancellation context
const reportAxiosErrorWithContext = (error) => {
  // Add cancellation breadcrumb if relevant
  const wasCanceled = addCancellationBreadcrumb(error);

  // If it's a pure cancellation with no server response, report as info
  if (wasCanceled && !error.response) {
    Sentry.captureMessage("Request canceled during navigation", {
      level: "info",
      tags: {
        errorType: "navigation_cancellation",
        errorName: error.name,
        errorCode: error.code,
        apiEndpoint: error.config?.url?.split("?")[0],
        method: error.config?.method?.toUpperCase(),
      },
      extra: {
        url: error.config?.url,
        method: error.config?.method,
        errorMessage: error.message,
        isNavigating: isNavigating,
        timeSinceNavigation: navigationStartTime
          ? Date.now() - navigationStartTime
          : null,
      },
    });
    return;
  }

  // Otherwise report the actual server error (breadcrumb provides cancellation context)
  reportAxiosError(error);
};

//fallback if there's no user yet- ie. they're not logged in yet
const getSessionClientId = () => {
  let sessionId = sessionStorage.getItem("session-client-id");
  if (!sessionId) {
    sessionId = `session-${uuidv4()}`;
    sessionStorage.setItem("session-client-id", sessionId);
  }
  return sessionId;
};

// Add these helper functions at the top of the file, after imports
const setGlobalClientId = (user) => {
  syncSentryUserFromAdsUser(user);

  if (user && user.id) {
    axios.defaults.headers.common["X-ClientId"] = `user-${user.id}`;
    axios.defaults.headers.common["X-Client-Id"] = `user-${user.id}`;
    console.info("X-ClientId header set for user:", user.id);
  } else if (user && user.ObjectId) {
    axios.defaults.headers.common["X-ClientId"] = `guid-${user.ObjectId}`;
    axios.defaults.headers.common["X-Client-Id"] = `guid-${user.ObjectId}`;
    console.info(
      "X-ClientId header set for user ObjectId:",
      user.ObjectId,
    );
  } else {
    // No user - use session UUID
    const sessionId = getSessionClientId();
    axios.defaults.headers.common["X-ClientId"] = sessionId;
    axios.defaults.headers.common["X-Client-Id"] = sessionId;
    console.info("X-ClientId header set for session:", sessionId);
  }
};

const setSessionClientId = () => {
  syncSentryUserFromAdsUser(null);

  const sessionId = getSessionClientId();
  axios.defaults.headers.common["X-ClientId"] = sessionId;
  axios.defaults.headers.common["X-Client-Id"] = sessionId;
  console.info("X-ClientId header set for session:", sessionId);
};

// Export these functions
export { getSessionClientId, setGlobalClientId, setSessionClientId };

// Global refresh de-duplication for proactive renew and 401 handling (DE-9510)
let refreshPromise = null;

const persistAccessToken = (accessToken) => {
  try {
    localStorage.setItem(
      `ads.auth.token.key${config.adsMsalConfig.clientID}`,
      JSON.stringify(accessToken),
    );
  } catch (_e) {
    // ignore quota/storage errors
  }
};

const buildSilentLoginRequest = (
  account,
  { forceRefresh = false } = {},
) => {
  const rawClaim =
    (account?.idTokenClaims &&
      (account.idTokenClaims.tfp || account.idTokenClaims.acr)) ||
    "";
  const isEmailPolicy =
    typeof rawClaim === "string" && /email/i.test(String(rawClaim));
  const authority = isEmailPolicy
    ? config.emailMsalConfig?.authority || config.adsMsalConfig.authority
    : config.adsMsalConfig.authority;
  return {
    scopes: ["openid", ...config.adsMsalConfig.b2cScopes],
    account,
    loginHint: account?.username,
    authority,
    forceRefresh,
    redirectUri: SILENT_REDIRECT_URI,
  };
};

/** Shared MSAL silent acquire; concurrent callers share one in-flight refresh. */
const acquireFreshAccessToken = (
  account,
  { forceRefresh = false } = {},
) => {
  if (skipSilentAcquire()) {
    const stored = getAccessTokenFromStorage();
    return stored
      ? Promise.resolve(stored)
      : Promise.reject(new Error("No stored bearer in Cypress"));
  }

  if (!refreshPromise) {
    refreshPromise = msalInstance
      .acquireTokenSilent(
        buildSilentLoginRequest(account, { forceRefresh }),
      )
      .then((tokenResponse) => {
        persistAccessToken(tokenResponse.accessToken);
        return tokenResponse.accessToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

const attachBearerToRequest = (requestConfig, accessToken) => {
  requestConfig.headers = {
    ...(requestConfig.headers || {}),
    Authorization: `Bearer ${accessToken}`,
  };
  const payload = tryParseJwt(accessToken);
  const expiresInMs = getTokenExpiresInMs(accessToken);
  console.info("AuthDebug: request", {
    method: requestConfig.method,
    url: requestConfig.url,
    hasBearer: true,
    aud: payload && payload.aud,
    expiresInMs,
  });
  return requestConfig;
};

// Maintain a single axios instance per baseURL to keep interceptors and defaults consistent
const clientByBaseUrl = new Map();

const createClient = (baseUrl = null) => {
  const options = { baseURL: baseUrl };
  const authToken = getAccessTokenFromStorage();
  if (authToken) {
    options.headers = { Authorization: `Bearer ${authToken}` };
  }

  const client = axios.create(options);

  // Add a request interceptor: ensure Authorization is present at send time
  client.interceptors.request.use(
    (requestConfig) => {
      const urlLower = String(requestConfig.url || "").toLowerCase();
      const isMaintenanceOrHealth =
        urlLower.includes("/maintenance") || urlLower.includes("/health");

      // Always send maintenance/health requests anonymously to avoid auth races
      if (isMaintenanceOrHealth) {
        if (requestConfig.headers) {
          delete requestConfig.headers.Authorization;
          delete requestConfig.headers["X-ClientId"];
          delete requestConfig.headers["X-Client-Id"];
        }
        return requestConfig;
      }

      const bearer = getAccessTokenFromStorage();
      if (bearer) {
        const shouldRenewBeforeSend = shouldProactivelyRenewBearer({
          bearer,
          msalInitialized,
          skipSilentAcquire: skipSilentAcquire(),
        });

        if (shouldRenewBeforeSend) {
          const account = getSessionMsalAccount(msalInstance);
          if (account) {
            console.info(
              "AuthDebug: token expiring soon - renewing before send",
              {
                method: requestConfig.method,
                url: requestConfig.url,
                expiresInMs: getTokenExpiresInMs(bearer),
              },
            );
            return acquireFreshAccessToken(account, { forceRefresh: true })
              .then((accessToken) =>
                attachBearerToRequest(requestConfig, accessToken),
              )
              .catch(() => attachBearerToRequest(requestConfig, bearer));
          }
        }

        return attachBearerToRequest(requestConfig, bearer);
      }

      // No token in storage: if MSAL not yet initialized, wait briefly, then retry token attach
      if (!msalInitialized) {
        console.info(
          "AuthDebug: waiting for MSAL init before protected request",
          {
            method: requestConfig.method,
            url: requestConfig.url,
          },
        );

        const waitForInit = new Promise((resolve) => {
          msalReady.then(() => resolve(true)).catch(() => resolve(false));
          // Safety timeout: proceed even if MSAL never resolves
          setTimeout(() => resolve(false), MSAL_INIT_WAIT_MS);
        });

        return waitForInit.then(() => {
          // Re-check for a bearer possibly stored by another flow
          const bearerNow = getAccessTokenFromStorage();
          if (bearerNow) {
            requestConfig.headers = {
              ...(requestConfig.headers || {}),
              Authorization: `Bearer ${bearerNow}`,
            };
            return requestConfig;
          }

          // If MSAL initialized, attempt silent acquire; otherwise proceed without bearer
          if (msalInitialized) {
            const account = getSessionMsalAccount(msalInstance);
            if (account) {
              if (skipSilentAcquire()) {
                return requestConfig;
              }
              return acquireFreshAccessToken(account, {
                forceRefresh: false,
              })
                .then((accessToken) =>
                  attachBearerToRequest(requestConfig, accessToken),
                )
                .catch(() => requestConfig);
            }
          }
          return requestConfig;
        });
      }
      const account = getSessionMsalAccount(msalInstance);
      if (account) {
        if (skipSilentAcquire()) {
          return requestConfig;
        }

        console.warn(
          "AuthDebug: request without bearer - attempting silent acquire",
          { method: requestConfig.method, url: requestConfig.url },
        );

        return acquireFreshAccessToken(account, { forceRefresh: false })
          .then((accessToken) =>
            attachBearerToRequest(requestConfig, accessToken),
          )
          .catch(() => {
            // Proceed without bearer; response interceptor will handle 401
            console.warn(
              "AuthDebug: silent acquire failed in request interceptor",
            );
            return requestConfig;
          });
      }

      console.warn(
        "AuthDebug: request without bearer and no MSAL account",
        {
          method: requestConfig.method,
          url: requestConfig.url,
        },
      );
      return requestConfig;
    },
    (requestError) => {
      // Request errors are typically configuration issues, log but don't report to Sentry
      console.error("Request configuration error:", requestError);
      return Promise.reject(requestError);
    },
  );

  // Add a response interceptor
  client.interceptors.response.use(
    (response) => {
      try {
        const responseUrl = String(response?.config?.url || "");
        if (
          responseUrl.includes("portal.api.") &&
          response?.status >= 200 &&
          response?.status < 300
        ) {
          markPortalApiRequestSuccess();
        }
      } catch (_markSuccess) {
        /* ignore */
      }

      // Detect unexpected HTML responses early and convert into actionable Sentry events
      try {
        const contentType =
          (response &&
            response.headers &&
            response.headers["content-type"]) ||
          (response &&
            response.headers &&
            response.headers["Content-Type"]) ||
          "";
        const typeLower = String(contentType).toLowerCase();
        const looksLikeHtml =
          typeLower.includes("text/html") ||
          (typeof response?.data === "string" &&
            (/^\s*<!doctype/i.test(response.data) ||
              /^\s*<html/i.test(response.data)));

        if (looksLikeHtml) {
          const endpoint = response?.config?.url?.split("?")[0];
          const method = response?.config?.method?.toUpperCase();
          const preview = (() => {
            try {
              const raw =
                typeof response.data === "string"
                  ? response.data
                  : JSON.stringify(response.data || "");
              return raw.slice(0, 200);
            } catch (_e) {
              return "<unavailable>";
            }
          })();

          Sentry.captureMessage(
            "HTML response detected for JSON request",
            {
              level: "error",
              tags: {
                errorType: "html_response",
                httpStatus: response?.status || 0,
                apiEndpoint: endpoint || "unknown_url",
                method: method || "UNKNOWN",
                contentType: contentType || "unknown",
              },
              extra: { preview },
            },
          );

          const htmlErr = new Error("HtmlResponseError");
          htmlErr.name = "HtmlResponseError";
          // Attach the response for downstream handlers that expect axios error shape
          // @ts-ignore
          htmlErr.response = response;
          throw htmlErr;
        }
      } catch (_e) {
        // best-effort guard; never block normal responses here
      }
      return response;
    },
    (error) => {
      // Cancellation
      if (isCanceledRequest(error) && !error.response) {
        reportAxiosErrorWithContext(error);
        return Promise.reject(error);
      }

      // If the server returned HTML (often an error page or auth redirect), report clearly
      try {
        const contentType =
          (error?.response?.headers &&
            (error.response.headers["content-type"] ||
              error.response.headers["Content-Type"])) ||
          "";
        const typeLower = String(contentType).toLowerCase();
        const data = error?.response?.data;
        const looksLikeHtml =
          typeLower.includes("text/html") ||
          (typeof data === "string" &&
            (/^\s*<!doctype/i.test(data) || /^\s*<html/i.test(data)));
        if (error.response && looksLikeHtml) {
          const endpoint = error.config?.url?.split("?")[0];
          const method = error.config?.method?.toUpperCase();
          const preview = (() => {
            try {
              const raw =
                typeof data === "string"
                  ? data
                  : JSON.stringify(data || "");
              return raw.slice(0, 200);
            } catch (_e) {
              return "<unavailable>";
            }
          })();
          Sentry.captureMessage("HTML response in axios error", {
            level: "error",
            tags: {
              errorType: "html_response",
              httpStatus: error.response?.status || 0,
              apiEndpoint: endpoint || "unknown_url",
              method: method || "UNKNOWN",
              contentType: contentType || "unknown",
            },
            extra: { preview },
          });
        }
      } catch (_e) {
        // non-fatal; continue with normal error handling flow
      }

      // Server errors and 404s
      const httpStatus = error.response?.status;
      if (httpStatus >= 500 || httpStatus === 404) {
        // Suppress generic network error alert for Maintenance endpoint;
        // maintenance flow will handle messaging/redirect separately.
        const isMaintenanceEndpoint =
          error?.config?.url && /Maintenance/i.test(error.config.url);
        reportAxiosErrorWithContext(error);
        if (!isMaintenanceEndpoint) {
          store.dispatch(
            userActions.errorStatus("Network Connection Error."),
          );
        }
      }

      // Why do we retry some 401s?
      // ---------------------------------
      // In real apps, a handful of 401s are caused by timing edges even when
      // the user is properly authenticated:
      //   - Token-expiry boundary / clock skew: token expires right as the
      //     request is sent, or the server clock is a bit ahead of the client.
      //   - Post-login race: first API call is made before the API-scoped token
      //     is persisted; a silent re-acquire fixes it.
      //   - Transient STS responses: the identity provider briefly returns
      //     invalid_token; a fresh token resolves it.
      //
      // To smooth out these UX paper-cuts without hiding real bugs, we allow a
      // single, narrowly-scoped refresh+retry when ALL of the following are true:
      //   1) The original request is idempotent (GET/HEAD/OPTIONS/PUT/DELETE),
      //      so replaying it won’t create duplicate side-effects.
      //   2) The request had a bearer token, proving we attempted authenticated
      //      access and this isn’t an anonymous call.
      //   3) The token is clearly the problem: it’s expired (per JWT exp), or the
      //      server’s WWW-Authenticate header indicates invalid/expired.
      //
      // Implementation notes:
      //   - One-shot only (guarded by _retry); no loops.
      //   - Uses msal.acquireTokenSilent({ forceRefresh: true }) to guarantee a
      //     fresh API-scoped token.
      //   - If refresh fails, we surface the 401 (or proceed to logout), keeping
      //     auth correctness strict.
      //
      // Net result: better reliability around token edges without masking
      // authorization issues or causing duplicate writes.
      // ---------------------------------
      // The guard below implements these conditions explicitly.
      // Retry only when ALL are true:
      //   - original request is idempotent (safe to replay)
      //   - original request had a bearer token
      //   - token is actually expired OR server says invalid/expired token
      if (error.response && error.response.status == 401) {
        const isLoggingOut = store.getState().authentication.loggingOut;
        const originalRequest = error.config || {};
        const hadBearer = Boolean(originalRequest?.headers?.Authorization);
        const currentHash =
          (typeof window !== "undefined" &&
            window.location &&
            window.location.hash) ||
          "";
        const isAuthFlowRoute = /#\/login|#\/loggingIn|#\/logout/i.test(
          currentHash,
        );
        const expired = isBearerExpired(
          originalRequest?.headers?.Authorization,
        );
        const invalidByServer = serverSaysInvalidToken(
          error.response && error.response.headers,
        );
        const eligibleForRefresh = shouldRetry401WithTokenRefresh({
          httpStatus: error.response.status,
          method: originalRequest.method,
          hadBearer,
          authorizationHeader: originalRequest?.headers?.Authorization,
          responseHeaders: error.response && error.response.headers,
          alreadyRetried: Boolean(originalRequest._retry),
          isLoggingOut,
          skipSilentAcquire: skipSilentAcquire(),
        });

        if (eligibleForRefresh) {
          if (!msalInitialized) {
            // Defer refresh until MSAL is ready; surface 401 normally
            return Promise.reject(error);
          }
          originalRequest._retry = true;

          const account = getSessionMsalAccount(msalInstance);
          if (account) {
            console.warn("AuthDebug: 401 before refresh (eligible)", {
              url: originalRequest && originalRequest.url,
              hadBearer,
              method: originalRequest && originalRequest.method,
              expired,
              invalidByServer,
            });

            return acquireFreshAccessToken(account, { forceRefresh: true })
              .then((accessToken) => {
                originalRequest.headers = {
                  ...(originalRequest.headers || {}),
                  Authorization: `Bearer ${accessToken}`,
                };
                const expiresInMs = getTokenExpiresInMs(accessToken);
                console.info("AuthDebug: refreshed token and retrying", {
                  url: originalRequest && originalRequest.url,
                  aud: tryParseJwt(accessToken)?.aud,
                  expiresInMs,
                });
                return client(originalRequest);
              })
              .catch(() => {
                if (!store.getState().authentication.loggingOut) {
                  store.dispatch(userActions.logout());
                }
                return Promise.reject(error);
              });
          }
        }

        // Force logout on 401 for protected endpoints when not in auth routes.
        // Previously we only did this if the request had a bearer; however if the
        // app lost MSAL state or token and we hit a protected API without a bearer,
        // we still need to log the user out.
        const urlLower = String(originalRequest?.url || "").toLowerCase();
        const isMaintenanceOrHealth =
          urlLower.includes("/maintenance") ||
          urlLower.includes("/health");
        // Only act on 401s coming from protected API endpoints
        const isProtected = isProtectedEndpoint(originalRequest?.url);
        if (
          !isLoggingOut &&
          !isAuthFlowRoute &&
          !isMaintenanceOrHealth &&
          isProtected
        ) {
          const fallback401 =
            !isCypressRuntime() && !getIsExchangesE2eSession()
              ? consumePostAuth401Fallback(currentHash)
              : null;
          if (fallback401) {
            store.dispatch(
              alertActions.warning(
                "That page could not be loaded with your account. Redirecting you.",
              ),
            );
            window.location.hash = fallback401;
            return Promise.reject(error);
          }
          store.dispatch(userActions.logout());
        }
      }

      return Promise.reject(error);
    },
  );

  return client;
};

const getClient = (baseUrl = null) => {
  const key = baseUrl || "__default__";
  if (clientByBaseUrl.has(key)) return clientByBaseUrl.get(key);
  const instance = createClient(baseUrl);
  clientByBaseUrl.set(key, instance);
  return instance;
};

class ApiClient {
  constructor(baseURL = null) {
    this.client = getClient(baseURL);
  }
  // API HTTP request implementations
  get(url, conf = {}) {
    return this.client
      .get(url, conf)
      .then((response) => Promise.resolve(response))
      .catch((error) => Promise.reject(error));
  }

  delete(url, conf = {}) {
    return this.client
      .delete(url, conf)
      .then((response) => Promise.resolve(response))
      .catch((error) => Promise.reject(error));
  }

  head(url, conf = {}) {
    return this.client
      .head(url, conf)
      .then((response) => Promise.resolve(response))
      .catch((error) => Promise.reject(error));
  }

  options(url, conf = {}) {
    return this.client
      .options(url, conf)
      .then((response) => Promise.resolve(response))
      .catch((error) => Promise.reject(error));
  }

  post(url, data = {}, conf = {}) {
    return this.client
      .post(url, data, conf)
      .then((response) => Promise.resolve(response))
      .catch((error) => {
        console.log(error);
        return Promise.reject(error);
      });
  }

  put(url, data = {}, conf = {}) {
    return this.client
      .put(url, data, conf)
      .then((response) => Promise.resolve(response))
      .catch((error) => Promise.reject(error));
  }

  patch(url, data = {}, conf = {}) {
    return this.client
      .patch(url, data, conf)
      .then((response) => Promise.resolve(response))
      .catch((error) => Promise.reject(error));
  }
}

export { ApiClient };

/**
 * Base HTTP Client
 */
//conf allows customisation of headers
export default {
  // Provide http request methods with the default base_url
  get(url, conf = {}) {
    // Check if we're running in Cypress
    const isCypress = getIsCypressAut();

    // Only use AbortController if not in Cypress
    let signal;
    let controller;
    let handleBeforeUnload;

    if (!isCypress) {
      controller = new AbortController();
      signal = controller.signal;

      handleBeforeUnload = () => {
        isNavigating = true;
        navigationStartTime = Date.now();
        controller.abort();
        console.info("Request aborted due to navigation");
      };

      window.addEventListener("beforeunload", handleBeforeUnload);
    }
    return getClient()
      .get(url, isCypress ? conf : { ...conf, signal })
      .then((response) => {
        // Only remove event listener if we added one
        if (!isCypress && handleBeforeUnload) {
          window.removeEventListener("beforeunload", handleBeforeUnload);
        }
        // Reset navigation state on successful response
        isNavigating = false;
        navigationStartTime = null;
        return response;
      })
      .catch((error) => {
        console.error(error);
        let errorSummary;
        if (error && typeof error === "object") {
          errorSummary = [
            `Error: ${error.message || "No message"}`,
            `Name: ${error.name || "N/A"}`,
            `Code: ${error.code || "N/A"}`,
            `Status: ${error.response?.status || "N/A"}`,
            `StatusText: ${error.response?.statusText || "N/A"}`,
            `URL: ${error.config?.url || "N/A"}`,
            `Method: ${error.config?.method || "N/A"}`,
            `Params: ${JSON.stringify(error.config?.params) || "N/A"}`,
            `Data: ${JSON.stringify(error.config?.data) || "N/A"}`,
            `ResponseData: ${JSON.stringify(error.response?.data) || "N/A"}`,
          ].join(" | ");
        } else {
          errorSummary = String(error);
        }
        console.error(errorSummary);
        // Only remove event listener if we added one
        if (!isCypress && handleBeforeUnload) {
          window.removeEventListener("beforeunload", handleBeforeUnload);
        }
        // Reset navigation state on error cleanup
        isNavigating = false;
        navigationStartTime = null;

        // Handle aborted/canceled requests with context
        if (!isCypress && isCanceledRequest(error)) {
          console.info("Request was aborted, likely due to navigation");
          // Don't report here - will be handled by axios interceptor or network error section
          return Promise.reject(error);
        }

        // Additional logging for status 0 errors
        if (!error.response && error.request) {
          console.warn("Network error detected:", {
            url: error.config?.url,
            method: error.config?.method,
            message: error.message,
            code: error.code,
            name: error.name,
            timestamp: new Date().toISOString(),
            readyState: error.request.readyState,
            status: error.request.status,
          });

          // Check if it's a timeout or other network issue
          if (error.code === "ECONNABORTED") {
            console.warn("Request timed out");
          } else if (error.code === "ENOTFOUND") {
            console.warn("DNS resolution failed");
          } else if (error.code === "ECONNRESET") {
            console.warn("Connection reset by server");
          } else if (error.code === "ECONNREFUSED") {
            console.warn("Connection refused by server");
          } else {
            console.warn("Unknown network error type");
          }
        }

        // Safety check for missing response object
        if (!error.response) {
          console.warn(
            "Error has no response object:",
            error.name,
            error.message,
          );

          // Handle network errors with cancellation context
          const wasCanceled = isCanceledRequest(error);
          if (wasCanceled) {
            // Report cancellation with context
            reportAxiosErrorWithContext(error);
          } else {
            // Skip network/infrastructure errors that frontend can't fix
            const networkInfrastructureErrors = [
              "ERR_NETWORK",
              "ECONNABORTED",
              "ETIMEDOUT",
              "ECONNRESET",
              "ECONNREFUSED",
              "ENOTFOUND",
            ];

            if (!networkInfrastructureErrors.includes(error.code)) {
              reportAxiosError(error);
            }
          }

          // Suppress generic alert for Maintenance endpoint; retry flow will handle
          const isMaintenanceEndpoint =
            error?.config?.url && /Maintenance/i.test(error.config.url);
          if (!isMaintenanceEndpoint) {
            store.dispatch(
              userActions.errorStatus(error.message || error),
            );
          }
          return Promise.reject(error);
        }

        // Handle 401 errors without reporting to Sentry (expected auth flow)
        if (
          error.response &&
          error.response.status == 401 &&
          store.getState().authentication.loggingOut
        ) {
          //prevent extra calls if user is already being kicked out, as extra calls cause sentry errors about interaction already being in progress
          store.dispatch(
            userActions.errorStatus(
              error.response &&
                error.response.data &&
                error.response.data.message
                ? error.response.data.message
                : error,
            ),
          );
        }
        //for the maintenance page as that call return 503 when portal should be down
        else if (
          error.response &&
          error.response.status == 503 &&
          error?.config?.url.includes("Maintenance")
        ) {
          markMaintenanceHttp503Observed("client.interceptor", {
            status: error.response.status,
          });
          store.dispatch(
            userActions.maintenanceMessage(
              "MAINTENANCE_MESSAGE",
              normalize503Payload(error.response.data),
            ),
          );
        } else if (error?.config?.url.includes("Maintenance")) {
          // For non-503 errors on the Maintenance endpoint, do not set global maintenance state
          // Let app-level retry logic decide to redirect after confirmation
          if (error && error.name === "BrowserAuthError") {
            return Promise.reject(error);
          }
          return Promise.reject(error);
        } else {
          // During logout flows, avoid pushing maintenance errors that can cause flicker
          const hash = window.location && window.location.hash;
          const isLoggingOut = store.getState().authentication.loggingOut;
          if (isLoggingOut || (hash && hash.includes("logout"))) {
            return Promise.reject(error);
          }
          store.dispatch(
            userActions.errorStatus(
              error.response &&
                error.response.data &&
                error.response.data.message
                ? error.response.data.message
                : error.response &&
                    error.response.data &&
                    error.response.data.error &&
                    error.response.data.error.message
                  ? error.response.data.error.message
                  : error,
            ),
          );
        }
        //we only get here if I check the state for if it's logging out, and it throws an axios error about the call failing
        if (store.getState().authentication.loggingOut) {
          console.log("401 logout already in progress");
        }
        return Promise.reject(error);
      });
  },

  delete(url, conf = {}) {
    return getClient()
      .delete(url, conf)
      .then((response) => Promise.resolve(response))
      .catch((error) => Promise.reject(error));
  },

  head(url, conf = {}) {
    return getClient()
      .head(url, conf)
      .then((response) => Promise.resolve(response))
      .catch((error) => Promise.reject(error));
  },

  options(url, conf = {}) {
    return getClient()
      .options(url, conf)
      .then((response) => Promise.resolve(response))
      .catch((error) => Promise.reject(error));
  },

  post(url, data = {}, conf = {}) {
    return getClient()
      .post(url, data, conf)
      .then((response) => Promise.resolve(response))
      .catch((error) => Promise.reject(error));
  },

  put(url, data = {}, conf = {}) {
    return getClient()
      .put(url, data, conf)
      .then((response) => Promise.resolve(response))
      .catch((error) => Promise.reject(error));
  },

  patch(url, data = {}, conf = {}) {
    return getClient()
      .patch(url, data, conf)
      .then((response) => Promise.resolve(response))
      .catch((error) => Promise.reject(error));
  },
};

const getErrorCategory = (status, isStatus0 = false) => {
  if (isStatus0) return "infrastructure";
  if (status >= 500) return "server_error";
  if (status === 404) return "not_found";
  if (status === 401) return "unauthorized";
  if (status === 403) return "forbidden";
  if (status >= 400) return "client_error";
  return "unknown";
};

const getErrorLevel = (status, isStatus0 = false) => {
  if (isStatus0) return "warning";
  if (status >= 500) return "error";
  if (status === 404) return "warning";
  if (status === 401) return "info";
  if (status === 403) return "warning";
  return "error";
};

const reportAxiosError = (error) => {
  const status = error.response?.status;
  const isNetworkError = !error.response && error.request;
  const isStatus0 = isNetworkError && error.request?.status === 0;

  // Unified error reporting
  Sentry.captureException(error, {
    tags: {
      errorType: isStatus0
        ? "network_status_0"
        : isNetworkError
          ? "network"
          : "http",
      httpStatus: status || 0,
      errorCategory: getErrorCategory(status, isStatus0),
      apiEndpoint: error.config?.url?.split("?")[0],
      method: error.config?.method?.toUpperCase(),
      baseURL: error.config?.baseURL || "",
      responseContentType:
        (error?.response?.headers &&
          (error.response.headers["content-type"] ||
            error.response.headers["Content-Type"])) ||
        "",
      ...(isStatus0 && { networkErrorType: error.code || "unknown" }),
    },
    fingerprint: [
      isStatus0
        ? "status_0_network_error"
        : isNetworkError
          ? "network_error"
          : "http_error",
      isStatus0
        ? error.code || "unknown_code"
        : status?.toString() || "no_status",
      error.config?.url?.split("?")[0] || "unknown_url",
    ],
    level: getErrorLevel(status, isStatus0),
    ...(isStatus0 && {
      extra: {
        errorName: error.name,
        errorCode: error.code,
        readyState: error.request?.readyState,
        timestamp: new Date().toISOString(),
      },
    }),
  });
};

// Recognize protected API hosts to scope 401→logout to authenticated services
const PROTECTED_API_HOSTS = new Set([
  "portal.api.next.cloud.ajw-group.com",
  "action.api.next.cloud.ajw-group.com",
  "ads.api.next.cloud.ajw-group.com",
  "ezylogisticstrackingapi.api.next.cloud.ajw-group.com",
]);

const isProtectedEndpoint = (url) => {
  if (!url) return false;
  try {
    const absolute = new URL(url, window.location.origin);
    return PROTECTED_API_HOSTS.has(absolute.hostname);
  } catch (_e) {
    // Not a valid URL; treat relative paths that clearly target APIs as protected
    const u = String(url).toLowerCase();
    return u.startsWith("/odata/") || u.startsWith("/api/");
  }
};
